import { Grid } from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  createDischargeSummary,
  getPetsCompliantSummaryWoLoader,
  updateInpatientAdmissionRemark,
} from "../../../../redux/reducers/petSlice";
import "./Discharge.css";
import {
  AGAINST_DOCTORS_ADVICE,
  dischargeMockData,
  patientOutcomeOptions,
} from "./mockDischargeData";

const formatPickerDate = (value) => {
  if (!value) return "";
  const parsed = moment(value?.$d ?? value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const formatPickerTime = (value) => {
  if (!value) return "";
  const parsed = moment(value?.$d ?? value);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const toPickerDate = (value) => (value ? dayjs(value) : null);

const toPickerTime = (value) => (value ? dayjs(`2000-01-01 ${value}`) : null);

const toDateValue = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const toTimeValue = (value) => {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/^(\d{2}:\d{2})/);
  return match?.[1] ?? "";
};

const getLatestDischargeSummary = (summaryData) => {
  const raw = summaryData?.dischargeSummary ?? summaryData ?? [];
  const list = Array.isArray(raw)
    ? raw.filter(Boolean)
    : raw && typeof raw === "object"
      ? [raw]
      : [];
  if (!list.length) return null;
  return [...list].sort((a, b) => {
    const aId = Number(a?.dischargeSummaryId ?? a?.id ?? 0);
    const bId = Number(b?.dischargeSummaryId ?? b?.id ?? 0);
    if (aId !== bId) return bId - aId;
    const aTime = new Date(
      a?.updatedDate ?? a?.createdDate ?? a?.createdAt ?? 0,
    ).getTime();
    const bTime = new Date(
      b?.updatedDate ?? b?.createdDate ?? b?.createdAt ?? 0,
    ).getTime();
    return bTime - aTime;
  })[0];
};

const mapDischargeSummaryToForm = (item, admissionRemark = "") => ({
  patientOutcome: item?.patientOutcome ?? "",
  finalDiagnosis: item?.finalDiagnosis ?? "",
  remark: admissionRemark ?? "",
  dietInstructions: item?.dietInstructions ?? "",
  activityRestrictions: item?.activityRestrictions ?? "",
  followUpDate: toDateValue(item?.followUpDate),
  ownerInstructions: item?.ownerInstructions ?? "",
  dateOfDeath: toDateValue(item?.dateOfDeath),
  timeOfDeath: toTimeValue(item?.timeOfDeath),
  circumstances: item?.circumstances ?? "",
});

const Discharge = ({ onSaveSuccess } = {}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { appointment } = location.state || {};
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const inpatData = petAndApntData?.inpatientResponse ?? {};
  const admissionRemark =
    reqDet?.inpatientResponse?.remarks ??
    inpatData?.remark ??
    inpatData?.remarks ??
    "";
  const appointmentId =
    reqDet?.appointment?.appointmentId ??
    appointment?.appoinment?.appointmentId ??
    appointment?.appointmentId ??
    null;
  const inpatientId =
    inpatData?.patientId ??
    reqDet?.inpatientResponse?.patientId ??
    reqDet?.appointment?.patientId ??
    null;
  const [formData, setFormData] = useState({
    ...dischargeMockData,
    remark: admissionRemark,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeNoteField, setActiveNoteField] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [showNoFieldModal, setShowNoFieldModal] = useState(false);

  const isDeceased = formData?.patientOutcome === "Deceased";
  const isAgainstAdvice = formData?.patientOutcome === AGAINST_DOCTORS_ADVICE;

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patientOutcome" && value !== "Deceased") {
        next.dateOfDeath = "";
        next.timeOfDeath = "";
        next.circumstances = "";
      }
      if (field === "patientOutcome" && value === "Deceased") {
        next.dietInstructions = "";
        next.activityRestrictions = "";
        next.followUpDate = "";
        next.ownerInstructions = "";
      }
      if (field === "patientOutcome" && value === AGAINST_DOCTORS_ADVICE) {
        if (!(next.remark ?? "").trim()) {
          next.remark = admissionRemark ?? "";
        }
      }
      return next;
    });
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleRecordNoteClick = () => {
    if (!activeNoteField) {
      setShowNoFieldModal(true);
      return;
    }

    setIsListening(true);
    setTimeout(() => {
      const voiceText = "Patient is eating well and recovering normally.";
      if (activeNoteField === "finalDiagnosis") {
        setFormData((prev) => ({
          ...prev,
          finalDiagnosis: prev.finalDiagnosis
            ? `${prev.finalDiagnosis}\n${voiceText}`
            : voiceText,
        }));
      } else if (activeNoteField === "remark") {
        setFormData((prev) => ({
          ...prev,
          remark: prev.remark ? `${prev.remark}\n${voiceText}` : voiceText,
        }));
      } else if (activeNoteField === "dietInstructions") {
        setFormData((prev) => ({
          ...prev,
          dietInstructions: prev.dietInstructions
            ? `${prev.dietInstructions}\n${voiceText}`
            : voiceText,
        }));
      } else if (activeNoteField === "activityRestrictions") {
        setFormData((prev) => ({
          ...prev,
          activityRestrictions: prev.activityRestrictions
            ? `${prev.activityRestrictions}\n${voiceText}`
            : voiceText,
        }));
      } else if (activeNoteField === "followUpDate") {
        const today = new Date().toISOString().split("T")[0];
        setFormData((prev) => ({ ...prev, followUpDate: today }));
      } else if (activeNoteField === "ownerInstructions") {
        setFormData((prev) => ({
          ...prev,
          ownerInstructions: prev.ownerInstructions
            ? `${prev.ownerInstructions}\n${voiceText}`
            : voiceText,
        }));
      } else if (activeNoteField === "patientOutcome") {
        setFormData((prev) => ({
          ...prev,
          patientOutcome: "Recovered — Discharged to owner",
        }));
      } else if (activeNoteField === "circumstances") {
        setFormData((prev) => ({
          ...prev,
          circumstances: prev.circumstances
            ? `${prev.circumstances}\n${voiceText}`
            : voiceText,
        }));
      } else if (activeNoteField === "dateOfDeath") {
        const today = new Date().toISOString().split("T")[0];
        setFormData((prev) => ({ ...prev, dateOfDeath: today }));
      }
      setIsListening(false);
    }, 1500);
  };

  const getSavePayload = () => ({
    patientOutcome: formData?.patientOutcome ?? "",
    finalDiagnosis: isAgainstAdvice ? "" : (formData?.finalDiagnosis ?? ""),
    dietInstructions: isDeceased ? "" : (formData?.dietInstructions ?? ""),
    activityRestrictions: isDeceased
      ? ""
      : (formData?.activityRestrictions ?? ""),
    followUpDate: isDeceased ? "" : formData?.followUpDate || "",
    ownerInstructions: isDeceased ? "" : (formData?.ownerInstructions ?? ""),
    dateOfDeath: isDeceased ? formData?.dateOfDeath || "" : "",
    timeOfDeath: isDeceased ? formData?.timeOfDeath || "" : "",
    circumstances: isDeceased ? (formData?.circumstances ?? "") : "",
  });

  const appendLatestSummary = (summaryPayload) => {
    const summaryData = summaryPayload?.data ?? summaryPayload;
    const latest = getLatestDischargeSummary(summaryData);
    if (!latest) return;
    setFormData(mapDischargeSummaryToForm(latest, admissionRemark));
    setErrors({});
  };

  useEffect(() => {
    if (!appointmentId) return;
    dispatch(getPetsCompliantSummaryWoLoader(appointmentId)).then((res) => {
      if (res?.payload) appendLatestSummary(res.payload);
    });
  }, [appointmentId]);

  useEffect(() => {
    if (!(admissionRemark ?? "").trim()) return;
    setFormData((prev) => {
      if ((prev?.remark ?? "").trim()) return prev;
      return { ...prev, remark: admissionRemark };
    });
  }, [admissionRemark]);

  const updateAdmissionRemark = () => {
    if (!inpatientId) return Promise.resolve(null);
    return dispatch(
      updateInpatientAdmissionRemark({
        inpatientId,
        remark: formData?.remark ?? "",
      }),
    );
  };

  const handleSaveChanges = () => {
    const nextErrors = {};
    if (!formData?.patientOutcome) {
      nextErrors.patientOutcome = "This field is required";
    }
    if (isAgainstAdvice) {
      if (!formData?.remark?.trim()) {
        nextErrors.remark = "This field is required";
      }
    } else if (!formData?.finalDiagnosis?.trim()) {
      nextErrors.finalDiagnosis = "This field is required";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !appointmentId) return;

    const payload = getSavePayload();
    setIsSaving(true);

    const saveDischarge = () =>
      dispatch(
        createDischargeSummary({
          appointmentId,
          data: payload,
        }),
      ).then((res) => {
        setIsSaving(false);
        if (res?.payload) {
          dispatch(getPetsCompliantSummaryWoLoader(appointmentId)).then(
            (summaryRes) => {
              if (summaryRes?.payload) appendLatestSummary(summaryRes.payload);
              if (typeof onSaveSuccess === "function") onSaveSuccess();
            },
          );
        }
      });

    if (isAgainstAdvice) {
      updateAdmissionRemark().then((remarkRes) => {
        if (remarkRes && !remarkRes?.payload) {
          setIsSaving(false);
          return;
        }
        saveDischarge();
      });
      return;
    }

    saveDischarge();
  };

  return (
    <div className="discharge-wrapper scroll-80vh w100Per">
      <div className="discharge-card">
        <div className="discharge-header">
          <div className="discharge-title-box">
            <h2 className="discharge-title">Discharge summary</h2>
            {/* <p className="discharge-subtitle">
              Click into any field below, then press Record Note — it fills
              whichever field you're in.
            </p> */}
          </div>
          {/* <div>
            {isListening ? (
              <div className="listening-badge">
                <span className="red-dot"></span>
                <span>Listening...</span>
              </div>
            ) : (
              <button
                className="record-note-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleRecordNoteClick}
              >
                <EditIcon style={{ fontSize: 16 }} />
                <span>Record Note</span>
              </button>
            )}
          </div> */}
        </div>

        <div
          style={{ marginBottom: 20 }}
          onClick={() => setActiveNoteField("patientOutcome")}
        >
          <label className="discharge-field-label">
            PATIENT OUTCOME
            <span className="discharge-field-required">*</span>
          </label>
          <CustomSelect
            select
            name="patientOutcome"
            label=""
            list={[
              ...patientOutcomeOptions.map((opt) => ({
                name: opt,
                value: opt,
              })),
            ]}
            value={formData.patientOutcome}
            handleChange={(e) =>
              handleInputChange("patientOutcome", e?.target?.value)
            }
            error={!!errors?.patientOutcome}
            helperText={errors?.patientOutcome}
          />
        </div>

        {isDeceased ? (
          <>
            <Grid container spacing={3} style={{ marginBottom: 20 }}>
              <Grid item xs={12} md={6}>
                <label className="discharge-field-label">DATE OF DEATH</label>
                <div onClick={() => setActiveNoteField("dateOfDeath")}>
                  <CustomTextField
                    label=""
                    fullWidth
                    mobileDate
                    name="dateOfDeath"
                    value={toPickerDate(formData.dateOfDeath)}
                    handleChange={(e) =>
                      handleInputChange(
                        "dateOfDeath",
                        formatPickerDate(e?.target?.value),
                      )
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <label className="discharge-field-label">TIME OF DEATH</label>
                <div onClick={() => setActiveNoteField("timeOfDeath")}>
                  <CustomTextField
                    label=""
                    fullWidth
                    mobileTime
                    is12HourFomat={false}
                    name="timeOfDeath"
                    value={toPickerTime(formData.timeOfDeath)}
                    handleChange={(e) =>
                      handleInputChange(
                        "timeOfDeath",
                        formatPickerTime(e?.target?.value),
                      )
                    }
                  />
                </div>
              </Grid>
            </Grid>
            <div style={{ marginBottom: 20 }}>
              <label className="discharge-field-label">CIRCUMSTANCES</label>
              <textarea
                className={`discharge-textarea ${
                  activeNoteField === "circumstances" ? "field-active" : ""
                }`}
                rows={3}
                placeholder="Brief clinical note on cause/circumstances"
                value={formData.circumstances}
                onFocus={() => setActiveNoteField("circumstances")}
                onClick={() => setActiveNoteField("circumstances")}
                onChange={(e) =>
                  handleInputChange("circumstances", e.target.value)
                }
              />
            </div>
          </>
        ) : null}

        <div style={{ marginBottom: 20 }}>
          {isAgainstAdvice ? (
            <>
              <label className="discharge-field-label">
                REASON FOR ADMISSION / PROVISIONAL DIAGNOSIS
                <span className="discharge-field-required">*</span>
              </label>
              <CustomTextField
                fullWidth
                multiline
                rows={3}
                handleChange={(e) =>
                  handleInputChange("remark", e?.target?.value)
                }
                required={true}
                value={formData?.remark ?? ""}
                error={!!errors?.remark}
                helperText={errors?.remark}
                placeholder="e.g. Post-op monitoring after entrotomy"
              />
            </>
          ) : (
            <>
              <label className="discharge-field-label">
                FINAL DIAGNOSIS
                <span className="discharge-field-required">*</span>
              </label>
              <textarea
                className={`discharge-textarea ${
                  activeNoteField === "finalDiagnosis" ? "field-active" : ""
                } ${errors?.finalDiagnosis ? "field-error" : ""}`}
                rows={4}
                value={formData.finalDiagnosis}
                onFocus={() => setActiveNoteField("finalDiagnosis")}
                onClick={() => setActiveNoteField("finalDiagnosis")}
                onChange={(e) =>
                  handleInputChange("finalDiagnosis", e.target.value)
                }
              />
              {errors?.finalDiagnosis ? (
                <div className="discharge-error">{errors.finalDiagnosis}</div>
              ) : null}
            </>
          )}
        </div>

        {!isDeceased ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: 20 }}>
                <label className="discharge-field-label">
                  DIET INSTRUCTIONS
                </label>
                <textarea
                  className={`discharge-textarea ${
                    activeNoteField === "dietInstructions" ? "field-active" : ""
                  }`}
                  rows={3}
                  placeholder="e.g. Bland diet for 5 days, then transition back gradually"
                  value={formData.dietInstructions}
                  onFocus={() => setActiveNoteField("dietInstructions")}
                  onClick={() => setActiveNoteField("dietInstructions")}
                  onChange={(e) =>
                    handleInputChange("dietInstructions", e.target.value)
                  }
                />
              </div>
              <div onClick={() => setActiveNoteField("followUpDate")}>
                <label className="discharge-field-label">FOLLOW-UP DATE</label>
                <CustomTextField
                  label=""
                  placeholder="FOLLOW-UP DATE"
                  fullWidth
                  mobileDate
                  name="followUpDate"
                  value={toPickerDate(formData.followUpDate)}
                  handleChange={(e) =>
                    handleInputChange(
                      "followUpDate",
                      formatPickerDate(e?.target?.value),
                    )
                  }
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: 20 }}>
                <label className="discharge-field-label">
                  ACTIVITY RESTRICTIONS
                </label>
                <textarea
                  className={`discharge-textarea ${
                    activeNoteField === "activityRestrictions"
                      ? "field-active"
                      : ""
                  }`}
                  rows={3}
                  placeholder="e.g. Leash walks only for 10 days, no jumping"
                  value={formData.activityRestrictions}
                  onFocus={() => setActiveNoteField("activityRestrictions")}
                  onClick={() => setActiveNoteField("activityRestrictions")}
                  onChange={(e) =>
                    handleInputChange("activityRestrictions", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="discharge-field-label">
                  OWNER INSTRUCTIONS / WARNING SIGNS TO WATCH FOR
                </label>
                <textarea
                  className={`discharge-textarea ${
                    activeNoteField === "ownerInstructions"
                      ? "field-active"
                      : ""
                  }`}
                  rows={3}
                  placeholder="e.g. Return immediately if vomiting or lethargy"
                  value={formData.ownerInstructions}
                  onFocus={() => setActiveNoteField("ownerInstructions")}
                  onClick={() => setActiveNoteField("ownerInstructions")}
                  onChange={(e) =>
                    handleInputChange("ownerInstructions", e.target.value)
                  }
                />
              </div>
            </Grid>
          </Grid>
        ) : null}

        <div>
          <button
            className="generate-summary-btn"
            onClick={handleSaveChanges}
            disabled={isSaving}
          >
            Save Changes
          </button>
        </div>
      </div>

      <CustomModal
        open={showNoFieldModal}
        onClose={() => setShowNoFieldModal(false)}
        modal
        headerShown
        headerCenter
        header="This page says"
        modalWidth={35}
      >
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <p
            style={{
              fontFamily: "Montserrat",
              fontSize: 14,
              color: "#333",
              marginBottom: 20,
            }}
          >
            Click into the field you want to fill first, then press Record Note.
          </p>
          <button
            className="modal-ok-btn"
            onClick={() => setShowNoFieldModal(false)}
          >
            OK
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default Discharge;
